// Builds an interactive collage from parsed pieces. Clicking/tapping a piece opens the
// photo in a large full-screen overlay (modal) where it's comfortably dragged/zoomed/rotated -
// the small collage is only for overview and opening pieces, no edits are made on it directly.
//
// This is the theme version of the original standalone editor - the logic is identical,
// only the CSS classes have an "se-" prefix (and the modal's "is-empty"/"is-open" ->
// "se-is-empty"/"se-is-open"), so they can't collide with the styles of the rest of the
// Shopify theme the editor is now embedded into as a block.

const SVG_NS = 'http://www.w3.org/2000/svg';

const ShapemoryEditor = (function () {
  let svgEl = null;
  let fileInput = null;
  let pieces = new Map(); // id -> piece state
  let pendingUploadPieceId = null;
  let modalPieceId = null; // which piece is currently open in the large overlay
  let viewBox = null; // [x, y, w, h] of the original SVG, for export
  // Unique prefix for ids generated per init() call (clipPath/filter) - id
  // attributes are global within the HTML document, not per-<svg> - without
  // this, two editors on one page with identically named Illustrator layers
  // (e.g. both "Layer_15", common with default Illustrator export) would
  // collide and some pieces would stop rendering (found in a comparison
  // harness where two instances of the same template ran at once - in real
  // production there is always exactly one editor on the page, so this
  // never happens there, but the fix is cheap and removes a latent risk).
  let instanceSeq = 0;
  let currentInstanceId = '';
  // Optional listener for the running counter of filled pieces (block.js) -
  // called whenever the number of filled pieces changes, with the same
  // data shape as getCompletionStatus(). For now the only trigger is adding
  // a photo (the editor has no way to empty a piece again), but the call is
  // centralized via notifyProgress() so a future "remove photo" feature can
  // hook into the same place.
  let progressListener = null;
  // Optional listener for "the exported collage would now look different"
  // (block.js) - fired on every mutation that affects pixels: a photo
  // loaded or replaced, and any pan/pinch/wheel/slider edit inside the
  // piece modal (zoom, rotation, brightness, contrast). block.js uses this
  // to invalidate an already-generated export/cart preview so a stale one
  // never gets uploaded after further edits.
  let changeListener = null;
  // Order exactly as in the original SVG document (fixed details and photo
  // pieces interleaved) - needed for export so it can draw in the same
  // z-order as it appears live in the DOM (e.g. a shadow on the nose must
  // stay above the photo).
  let orderedItems = [];

  // Modal DOM (the editor builds and manages it itself, appends to <body>)
  let modalRoot, modalSvg, modalArtGroup, modalZoomSlider, modalZoomVal, modalRotSlider, modalRotVal;
  let modalBrightnessSlider, modalBrightnessVal, modalContrastSlider, modalContrastVal;

  function el(tag, attrs) {
    const e = document.createElementNS(SVG_NS, tag);
    if (attrs) for (const k in attrs) e.setAttribute(k, attrs[k]);
    return e;
  }

  function h(tag, attrs, children) {
    const e = document.createElement(tag);
    if (attrs) for (const k in attrs) {
      if (k === 'class') e.className = attrs[k];
      else e.setAttribute(k, attrs[k]);
    }
    (children || []).forEach((c) => e.appendChild(c));
    return e;
  }

  function unionBBox(elements) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const e of elements) {
      const b = e.getBBox();
      minX = Math.min(minX, b.x);
      minY = Math.min(minY, b.y);
      maxX = Math.max(maxX, b.x + b.width);
      maxY = Math.max(maxY, b.y + b.height);
    }
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }

  function clamp(v, min, max) { return Math.min(max, Math.max(min, v)); }

  function applyTransform(piece) {
    const cx = piece.imgCenterX;
    const cy = piece.imgCenterY;
    const t = `translate(${piece.panX} ${piece.panY}) translate(${cx} ${cy}) rotate(${piece.rotation}) scale(${piece.scale}) translate(${-cx} ${-cy})`;
    piece.transformGroups.forEach((g) => g.setAttribute('transform', t));
  }

  // Brightness/contrast via a native SVG filter (not CSS) - this way it's part of the
  // SVG document itself and gets carried over automatically on export (cloning +
  // serialization), exactly like clip-path. Each piece has its own filter, since
  // different photos need different adjustment.
  function applyPieceFilter(piece) {
    const contrastFactor = piece.contrast / 100;
    const brightnessOffset = (piece.brightness - 100) / 200;
    const slope = contrastFactor;
    const intercept = 0.5 * (1 - contrastFactor) + brightnessOffset;
    piece.filterFuncs.forEach((f) => {
      f.setAttribute('slope', slope);
      f.setAttribute('intercept', intercept);
    });
  }

  function setEmptyVisual(piece, isEmpty) {
    piece.outlineElements.forEach((oEl) => oEl.classList.toggle('se-is-empty', isEmpty));
    piece.plusMarks.forEach((m) => { m.style.display = isEmpty ? '' : 'none'; });
  }

  function fitImageToBBox(piece, naturalW, naturalH) {
    const bbox = piece.bbox;
    const coverScale = Math.max(bbox.width / naturalW, bbox.height / naturalH);
    const overscan = 1.3;
    const w = naturalW * coverScale * overscan;
    const h2 = naturalH * coverScale * overscan;
    const x = bbox.x + bbox.width / 2 - w / 2;
    const y = bbox.y + bbox.height / 2 - h2 / 2;
    piece.imageElements.forEach((img) => {
      img.setAttribute('x', x);
      img.setAttribute('y', y);
      img.setAttribute('width', w);
      img.setAttribute('height', h2);
    });
    // Duplicated with the attributes on the <image> above - export draws
    // directly from these values via Canvas 2D (drawImage), not from
    // DOM/SVG attributes.
    piece.imageX = x;
    piece.imageY = y;
    piece.imageW = w;
    piece.imageH = h2;
    piece.imgCenterX = x + w / 2;
    piece.imgCenterY = y + h2 / 2;
    piece.panX = 0;
    piece.panY = 0;
    piece.scale = 1;
    piece.rotation = 0;
    piece.brightness = 100;
    piece.contrast = 100;
    applyPieceFilter(piece);
  }

  function syncModalControls(piece) {
    modalZoomSlider.value = piece.scale;
    modalZoomVal.textContent = piece.scale.toFixed(2);
    modalRotSlider.value = Math.round(piece.rotation);
    modalRotVal.textContent = Math.round(piece.rotation);
    modalBrightnessSlider.value = piece.brightness;
    modalBrightnessVal.textContent = Math.round(piece.brightness);
    modalContrastSlider.value = piece.contrast;
    modalContrastVal.textContent = Math.round(piece.contrast);
  }

  // Photos straight from a phone can easily be several MB (12+ Mpx) - without
  // downscaling they'd be embedded as full-size base64 in a single exported SVG
  // (19+ pieces), which can reliably crash rasterization on Safari for large
  // <image href="data:..."> elements inside a separately loaded SVG document
  // (exactly the path export goes through). The collage is exported at only
  // 3000px total width anyway, so a single piece never needs more than a few
  // hundred px on its longer side - 1600px is comfortably enough even with
  // heavy zoom/cropping in the editor.
  const MAX_PHOTO_DIMENSION = 1600;

  function downscaleImageFile(file) {
    return new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(file);
      const probe = new Image();
      probe.onload = () => {
        const scale = Math.min(1, MAX_PHOTO_DIMENSION / Math.max(probe.naturalWidth, probe.naturalHeight));
        const w = Math.round(probe.naturalWidth * scale);
        const h = Math.round(probe.naturalHeight * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(probe, 0, 0, w, h);
        URL.revokeObjectURL(objectUrl);
        resolve({ dataUrl: canvas.toDataURL('image/jpeg', 0.85), width: w, height: h });
      };
      probe.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Failed to load the photo'));
      };
      probe.src = objectUrl;
    });
  }

  function loadImageIntoPiece(piece, dataUrl, naturalW, naturalH) {
    fitImageToBBox(piece, naturalW, naturalH);
    piece.imageElements.forEach((img) => {
      img.setAttribute('href', dataUrl);
      img.setAttribute('xlink:href', dataUrl);
    });
    // Export draws the photo directly from this data URL via Canvas 2D
    // (independent of the <image> elements in the SVG - see getExportModel).
    piece.imageDataUrl = dataUrl;
    applyTransform(piece);
    piece.hasImage = true;
    setEmptyVisual(piece, false);
    notifyProgress();
    notifyChange();
    if (modalPieceId === piece.id) {
      syncModalControls(piece); // modal is already open (e.g. "Change photo") - just refresh
    } else {
      openModal(piece.id);
    }
  }

  function handleFileChosen(file) {
    if (!file || !pendingUploadPieceId) return;
    const piece = pieces.get(pendingUploadPieceId);
    downscaleImageFile(file)
      .then(({ dataUrl, width, height }) => loadImageIntoPiece(piece, dataUrl, width, height))
      .catch((err) => console.error('Failed to process the selected photo:', err));
  }

  function onPieceClick(pieceId) {
    const piece = pieces.get(pieceId);
    if (!piece.hasImage) {
      pendingUploadPieceId = pieceId;
      fileInput.click();
    } else {
      openModal(pieceId);
    }
  }

  // --- Modal: moves a piece (its photo layer) into the large overlay and back ---

  function openModal(pieceId) {
    const piece = pieces.get(pieceId);
    if (!piece || !piece.hasImage || modalPieceId) return;

    modalPieceId = pieceId;
    piece.placeholders = piece.clipGroups.map((g) => {
      const anchor = document.createComment('piece-anchor:' + pieceId);
      g.parentNode.insertBefore(anchor, g);
      modalArtGroup.appendChild(g);
      return anchor;
    });

    const bbox = piece.bbox;
    const margin = Math.max(bbox.width, bbox.height) * 0.15;
    modalSvg.setAttribute('viewBox', `${bbox.x - margin} ${bbox.y - margin} ${bbox.width + margin * 2} ${bbox.height + margin * 2}`);
    modalSvg.style.aspectRatio = `${bbox.width + margin * 2} / ${bbox.height + margin * 2}`;

    resetModalGesture();
    syncModalControls(piece);
    modalRoot.classList.add('se-is-open');
    // ensures the "position: fixed" overlay always lands exactly on the full
    // screen regardless of where the main page had scrolled to before.
    window.scrollTo(0, 0);
  }

  function closeModal() {
    if (!modalPieceId) return;
    const piece = pieces.get(modalPieceId);
    piece.clipGroups.forEach((g, i) => {
      const anchor = piece.placeholders[i];
      anchor.parentNode.insertBefore(g, anchor);
      anchor.remove();
    });
    piece.placeholders = [];
    modalPieceId = null;
    modalRoot.classList.remove('se-is-open');
  }

  function replaceModalPieceImage() {
    if (!modalPieceId) return;
    pendingUploadPieceId = modalPieceId;
    fileInput.click();
  }

  // --- Gestures inside the modal: one-finger/mouse drag, two-finger pinch = zoom+rotate ---
  const activePointers = new Map();
  let dragLast = null;
  let pinchStart = null;

  function resetModalGesture() {
    activePointers.clear();
    dragLast = null;
    pinchStart = null;
  }

  function modalSvgPoint(clientX, clientY) {
    const ctm = modalSvg.getScreenCTM().inverse();
    const pt = modalSvg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    return pt.matrixTransform(ctm);
  }

  function pointersArray() { return Array.from(activePointers.values()); }

  function onModalPointerDown(e) {
    if (!modalPieceId) return;
    modalSvg.setPointerCapture(e.pointerId);
    activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (activePointers.size === 1) {
      const p = modalSvgPoint(e.clientX, e.clientY);
      dragLast = { x: p.x, y: p.y };
    } else if (activePointers.size === 2) {
      const piece = pieces.get(modalPieceId);
      const [a, b] = pointersArray();
      pinchStart = {
        dist: Math.hypot(a.x - b.x, a.y - b.y),
        angle: Math.atan2(b.y - a.y, b.x - a.x) * 180 / Math.PI,
        scale: piece.scale,
        rotation: piece.rotation,
      };
    }
  }

  function onModalPointerMove(e) {
    if (!modalPieceId || !activePointers.has(e.pointerId)) return;
    activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const piece = pieces.get(modalPieceId);

    if (activePointers.size === 1 && dragLast) {
      const p = modalSvgPoint(e.clientX, e.clientY);
      piece.panX += p.x - dragLast.x;
      piece.panY += p.y - dragLast.y;
      dragLast = { x: p.x, y: p.y };
      applyTransform(piece);
      notifyChange();
    } else if (activePointers.size === 2 && pinchStart) {
      const [a, b] = pointersArray();
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const angle = Math.atan2(b.y - a.y, b.x - a.x) * 180 / Math.PI;
      piece.scale = clamp(pinchStart.scale * (dist / pinchStart.dist), 0.2, 5);
      piece.rotation = pinchStart.rotation + (angle - pinchStart.angle);
      applyTransform(piece);
      syncModalControls(piece);
      notifyChange();
    }
  }

  function onModalPointerUp(e) {
    activePointers.delete(e.pointerId);
    try { modalSvg.releasePointerCapture(e.pointerId); } catch (err) {}
    if (activePointers.size === 1) {
      const [a] = pointersArray();
      const p = modalSvgPoint(a.x, a.y);
      dragLast = { x: p.x, y: p.y };
      pinchStart = null;
    } else if (activePointers.size === 0) {
      dragLast = null;
      pinchStart = null;
    }
  }

  function onModalWheel(e) {
    if (!modalPieceId) return;
    const piece = pieces.get(modalPieceId);
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.05 : 0.95;
    piece.scale = clamp(piece.scale * factor, 0.2, 5);
    applyTransform(piece);
    syncModalControls(piece);
    notifyChange();
  }

  // --- Building the modal DOM (the editor builds it itself, appends to <body>) ---

  function buildModal() {
    modalSvg = el('svg', { class: 'se-modal-stage' });
    modalArtGroup = el('g');
    modalSvg.appendChild(modalArtGroup);

    modalZoomVal = h('span', {}, [document.createTextNode('1.00')]);
    modalRotVal = h('span', {}, [document.createTextNode('0')]);
    modalZoomSlider = h('input', { type: 'range', min: '0.2', max: '5', step: '0.01', value: '1' });
    modalRotSlider = h('input', { type: 'range', min: '-180', max: '180', step: '1', value: '0' });

    modalBrightnessVal = h('span', {}, [document.createTextNode('100')]);
    modalContrastVal = h('span', {}, [document.createTextNode('100')]);
    modalBrightnessSlider = h('input', { type: 'range', min: '50', max: '150', step: '1', value: '100' });
    modalContrastSlider = h('input', { type: 'range', min: '50', max: '150', step: '1', value: '100' });

    const replaceBtn = h('button', {}, [document.createTextNode('Change photo')]);
    const doneBtn = h('button', { class: 'se-modal-done-btn' }, [document.createTextNode('Done')]);

    modalZoomSlider.addEventListener('input', () => {
      if (!modalPieceId) return;
      const piece = pieces.get(modalPieceId);
      piece.scale = clamp(parseFloat(modalZoomSlider.value), 0.2, 5);
      modalZoomVal.textContent = piece.scale.toFixed(2);
      applyTransform(piece);
      notifyChange();
    });
    modalRotSlider.addEventListener('input', () => {
      if (!modalPieceId) return;
      const piece = pieces.get(modalPieceId);
      piece.rotation = parseFloat(modalRotSlider.value);
      modalRotVal.textContent = Math.round(piece.rotation);
      applyTransform(piece);
      notifyChange();
    });
    modalBrightnessSlider.addEventListener('input', () => {
      if (!modalPieceId) return;
      const piece = pieces.get(modalPieceId);
      piece.brightness = parseFloat(modalBrightnessSlider.value);
      modalBrightnessVal.textContent = Math.round(piece.brightness);
      applyPieceFilter(piece);
      notifyChange();
    });
    modalContrastSlider.addEventListener('input', () => {
      if (!modalPieceId) return;
      const piece = pieces.get(modalPieceId);
      piece.contrast = parseFloat(modalContrastSlider.value);
      modalContrastVal.textContent = Math.round(piece.contrast);
      applyPieceFilter(piece);
      notifyChange();
    });
    replaceBtn.addEventListener('click', replaceModalPieceImage);
    doneBtn.addEventListener('click', closeModal);

    const controls = h('div', { class: 'se-modal-controls' }, [
      h('div', { class: 'se-control-row' }, [h('label', {}, [document.createTextNode('Zoom ('), modalZoomVal, document.createTextNode('x)')]), modalZoomSlider]),
      h('div', { class: 'se-control-row' }, [h('label', {}, [document.createTextNode('Rotation ('), modalRotVal, document.createTextNode('°)')]), modalRotSlider]),
      h('div', { class: 'se-control-row' }, [h('label', {}, [document.createTextNode('Brightness ('), modalBrightnessVal, document.createTextNode('%)')]), modalBrightnessSlider]),
      h('div', { class: 'se-control-row' }, [h('label', {}, [document.createTextNode('Contrast ('), modalContrastVal, document.createTextNode('%)')]), modalContrastSlider]),
      h('div', { class: 'se-toolbar-buttons' }, [replaceBtn, doneBtn]),
    ]);

    const stageWrap = h('div', { class: 'se-modal-stage-wrap' }, [modalSvg]);
    modalRoot = h('div', { class: 'se-piece-modal' }, [stageWrap, controls]);
    document.body.appendChild(modalRoot);

    modalSvg.addEventListener('pointerdown', onModalPointerDown);
    modalSvg.addEventListener('pointermove', onModalPointerMove);
    modalSvg.addEventListener('pointerup', onModalPointerUp);
    modalSvg.addEventListener('pointercancel', onModalPointerUp);
    modalSvg.addEventListener('wheel', onModalWheel, { passive: false });
  }

  // --- Building the main collage ---

  // artLayer carries the actual visual output (both black details and photos) in exactly
  // the order the pieces appeared in the original SVG - some non-editable details need to
  // stay on top of the photo of the same piece (e.g. a shadow on the nose), so the order
  // must not change. interactionLayer is a separate invisible layer on top, just for clicking.
  function buildPieceDom(pieceData, defsEl, artLayer, interactionLayer, viewBoxDiag) {
    if (pieceData.type === 'fixed') {
      const shape = pieceData.shapes[0].cloneNode(false);
      shape.removeAttribute('fill');
      shape.setAttribute('fill', '#000');
      artLayer.appendChild(shape);
      // the original (unedited) source element - export fills the black
      // silhouette directly from it, no clone needed.
      orderedItems.push({ kind: 'fixed', shape: pieceData.shapes[0] });
      return null;
    }

    const piece = {
      id: pieceData.id,
      type: pieceData.type,
      hasImage: false,
      // The original (unedited) source shapes for this piece, in the
      // coordinates of the whole SVG - export builds a clip path (Path2D)
      // directly from these, independent of the clones below, which serve
      // only the live DOM (clipPath/hit/outline).
      shapes: pieceData.shapes,
      clipGroups: [],
      placeholders: [],
      transformGroups: [],
      imageElements: [],
      hitElements: [],
      outlineElements: [],
      plusMarks: [],
      panX: 0, panY: 0, scale: 1, rotation: 0,
      brightness: 100, contrast: 100, filterFuncs: null,
      imgCenterX: 0, imgCenterY: 0,
      bbox: null,
    };

    // One shared brightness/contrast filter for the whole piece (even if it
    // has multiple shapes, e.g. a linked group) - the photo should be
    // adjusted the same way across all of them.
    const filterId = `filter_${currentInstanceId}_${pieceData.id}`;
    const filterEl = el('filter', { id: filterId, 'color-interpolation-filters': 'sRGB' });
    const transferEl = el('feComponentTransfer');
    const funcR = el('feFuncR', { type: 'linear', slope: '1', intercept: '0' });
    const funcG = el('feFuncG', { type: 'linear', slope: '1', intercept: '0' });
    const funcB = el('feFuncB', { type: 'linear', slope: '1', intercept: '0' });
    transferEl.appendChild(funcR);
    transferEl.appendChild(funcG);
    transferEl.appendChild(funcB);
    filterEl.appendChild(transferEl);
    defsEl.appendChild(filterEl);
    piece.filterFuncs = [funcR, funcG, funcB];

    pieceData.shapes.forEach((sourceShape, i) => {
      const clipId = `clip_${currentInstanceId}_${pieceData.id}_${i}`;
      const clipPath = el('clipPath', { id: clipId, clipPathUnits: 'userSpaceOnUse' });
      clipPath.appendChild(sourceShape.cloneNode(false));
      defsEl.appendChild(clipPath);

      const clipGroup = el('g', { 'clip-path': `url(#${clipId})` });
      // A white "backing" in exactly the shape of the piece - without it, an
      // empty piece would just show the canvas background color through it
      // and blend into it. Lives in artLayer (in the same order as the piece
      // itself), so non-editable details on top remain correctly visible.
      // Once a photo is uploaded it covers the backing; it never makes it
      // into the export (export always strips it).
      const backdrop = sourceShape.cloneNode(false);
      backdrop.setAttribute('class', 'se-piece-backdrop');
      clipGroup.appendChild(backdrop);
      const transformGroup = el('g');
      // Both "href" and "xlink:href" are set - the live SVG in the DOM works
      // fine with plain "href", but when the SVG is later serialized on
      // export and reloaded as a standalone image resource (exactly what
      // export.js does), Safari requires the older namespaced "xlink:href"
      // on <image>, otherwise it silently ignores it and the photo is
      // missing from the export even though it's visible live in the
      // editor.
      const image = el('image', { href: '', 'xlink:href': '', filter: `url(#${filterId})` });
      transformGroup.appendChild(image);
      clipGroup.appendChild(transformGroup);
      artLayer.appendChild(clipGroup);

      // An invisible element purely for clicking/tapping - its stroke-width
      // is enlarged for small pieces (a tolerance margin), but stroke-opacity
      // always stays 0.
      const hit = sourceShape.cloneNode(false);
      hit.setAttribute('fill', 'none');
      hit.setAttribute('stroke', '#000');
      hit.setAttribute('stroke-opacity', '0');
      hit.setAttribute('class', 'se-piece-hit');
      hit.setAttribute('pointer-events', 'all');
      hit.setAttribute('data-piece', pieceData.id);
      interactionLayer.appendChild(hit);

      // A separate, purely visual element for a thin continuous divider
      // line on an empty slot - its thickness is always the same thin
      // value, regardless of the tolerance margin above.
      const outline = sourceShape.cloneNode(false);
      outline.setAttribute('fill', 'none');
      outline.setAttribute('stroke-width', viewBoxDiag * 0.0015);
      outline.setAttribute('class', 'se-piece-outline se-is-empty');
      outline.setAttribute('pointer-events', 'none');
      interactionLayer.appendChild(outline);

      piece.clipGroups.push(clipGroup);
      piece.transformGroups.push(transformGroup);
      piece.imageElements.push(image);
      piece.hitElements.push(hit);
      piece.outlineElements.push(outline);
    });

    piece.bbox = unionBBox(piece.hitElements);

    // For small pieces, the visual shape is too small for a reliable finger
    // tap - adds an invisible "tolerance" margin (via stroke-width only on
    // the "hit" element, never on the visible "outline") just for the
    // smallest pieces, large areas are unchanged. Scales with the overall
    // SVG dimensions, not a fixed number, so it works for other
    // (larger/smaller) future SVG files too.
    const minDim = Math.min(piece.bbox.width, piece.bbox.height);
    const targetMinTap = viewBoxDiag * 0.06;
    const toleranceWidth = Math.max(0, targetMinTap - minDim);
    piece.hitElements.forEach((hEl) => hEl.setAttribute('stroke-width', toleranceWidth));

    // No "+" mark on an empty slot - intentionally removed (there used to be
    // a <text>+</text> here, sized relative to viewBoxDiag/minDim). Since an
    // empty slot now has its own color (--se-piece-bg, distinct from the
    // white canvas), that alone is enough of a signal that "this is
    // clickable" - together with the thin continuous piece outline
    // (.se-piece-outline.se-is-empty) below. `piece.plusMarks` stays (an
    // empty array) - `setEmptyVisual()` safely calls `.forEach()` on it
    // regardless of whether anything is inside.

    orderedItems.push({ kind: 'piece', piece });
    return piece;
  }

  async function init(containerEl, svgUrl) {
    currentInstanceId = `i${instanceSeq++}`;
    const res = await fetch(svgUrl);
    const svgText = await res.text();
    const parsed = parseShapemorySvg(svgText);
    const [vx, vy, vw, vh] = parsed.viewBox;
    viewBox = parsed.viewBox; // exact original viewBox - export is based on it, never changes
    orderedItems = [];

    const viewBoxDiag = Math.hypot(vw, vh);

    // Visible gap between the silhouette and the canvas frame, so it can
    // "breathe" - the canvas has its own subtle gray border around the
    // whole square/rectangular area (CSS border on .se-collage-stage), the
    // silhouette isn't pressed right up to the edge. The margin is
    // proportional to the larger dimension of the artwork (not a fixed
    // number), so the spacing looks consistent across templates of very
    // different scale.
    // (There used to also be a tight outline tracing the silhouette exactly
    // here - intentionally removed, it was misleading, as if it were part
    // of the printed collage; in reality there's no outline there.)
    const stageMargin = Math.max(vw, vh) * 0.09;
    const displayVB = [
      vx - stageMargin,
      vy - stageMargin,
      vw + stageMargin * 2,
      vh + stageMargin * 2,
    ];

    svgEl = el('svg', {
      viewBox: displayVB.join(' '),
      class: 'se-collage-stage',
    });
    svgEl.style.aspectRatio = `${displayVB[2]} / ${displayVB[3]}`;

    const defsEl = el('defs');
    const artLayer = el('g', { class: 'se-layer-art' });
    const interactionLayer = el('g', { class: 'se-layer-interaction' });
    svgEl.appendChild(defsEl);
    svgEl.appendChild(artLayer);
    svgEl.appendChild(interactionLayer);

    containerEl.innerHTML = '';
    containerEl.appendChild(svgEl);

    fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    // "display:none" can be unreliable on Android (input.click() doesn't
    // always fire right away on the first touch, and native UI for "file
    // attached" can pop up) - instead we just visually hide it, the element
    // stays technically "present".
    fileInput.className = 'se-visually-hidden-input';
    fileInput.addEventListener('change', () => {
      handleFileChosen(fileInput.files[0]);
      fileInput.value = '';
      fileInput.blur();
    });
    containerEl.appendChild(fileInput);

    pieces = new Map();
    let fixedCount = 0;
    for (const pieceData of parsed.pieces) {
      const piece = buildPieceDom(pieceData, defsEl, artLayer, interactionLayer, viewBoxDiag);
      if (piece) pieces.set(piece.id, piece);
      else fixedCount += 1;
    }

    // "click" (not "pointerdown") - the main collage no longer needs to
    // track dragging, so a reliable standard tap/click is enough, which
    // mobile browsers unambiguously recognize as a real tap (important for
    // triggering the native file picker on the very first touch).
    svgEl.addEventListener('click', (e) => {
      const target = e.target.closest('[data-piece]');
      if (target) onPieceClick(target.getAttribute('data-piece'));
    });

    buildModal();

    return {
      svgEl,
      pieceCount: pieces.size,
      fixedCount,
    };
  }

  // Static description of the current collage state, independent of the SVG
  // DOM - export builds the resulting PNG directly from it via Canvas 2D
  // (drawImage/clip), instead of serializing and reloading the SVG as an
  // image (see shapemory-export.js for why we deliberately avoid that on
  // Safari).
  function getExportModel() {
    return {
      viewBox,
      items: orderedItems.map((item) => {
        if (item.kind === 'fixed') return { kind: 'fixed', shape: item.shape };
        const p = item.piece;
        return {
          kind: 'piece',
          hasImage: p.hasImage,
          shapes: p.shapes,
          bbox: p.bbox,
          imageDataUrl: p.imageDataUrl,
          imageX: p.imageX,
          imageY: p.imageY,
          imageW: p.imageW,
          imageH: p.imageH,
          transform: {
            panX: p.panX,
            panY: p.panY,
            scale: p.scale,
            rotation: p.rotation,
            imgCenterX: p.imgCenterX,
            imgCenterY: p.imgCenterY,
          },
          filter: {
            slope: parseFloat(p.filterFuncs[0].getAttribute('slope')),
            intercept: parseFloat(p.filterFuncs[0].getAttribute('intercept')),
          },
        };
      }),
    };
  }

  // How many photo pieces (not fixed black details - those can never be
  // filled) still don't have a photo - used by "block.js" to block
  // continuing to "Add to cart" until the collage is complete, and for the
  // running counter in the UI (see progressListener above).
  function getCompletionStatus() {
    let total = 0;
    let filled = 0;
    pieces.forEach((piece) => {
      total += 1;
      if (piece.hasImage) filled += 1;
    });
    return { total, filled, missing: total - filled };
  }

  function notifyProgress() {
    if (progressListener) progressListener(getCompletionStatus());
  }

  function setProgressListener(fn) {
    progressListener = fn;
  }

  function notifyChange() {
    if (changeListener) changeListener();
  }

  function setChangeListener(fn) {
    changeListener = fn;
  }

  return {
    init,
    getExportModel,
    getCompletionStatus,
    setProgressListener,
    setChangeListener,
  };
})();
